import { useMutation, useQuery } from '@apollo/client/react';
import { startAuthentication } from '@simplewebauthn/browser';
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
  MFA_EMAIL_OTP_CONTRACT,
  MFA_FACTOR,
  MFA_RECOVERY_METHOD,
  MFA_SANITIZED_ERROR_CODE,
  type MfaFactor,
  OAUTH_REAUTH_ERROR,
  OAUTH_REAUTH_ERROR_PARAM,
  type OAuthReauthError,
  parseOAuthReauthError,
  TIME,
} from '@repo/common';
import {
  BeginMfaPasskeyReauthDocument,
  ConfirmMfaEnrollmentDocument,
  ConfirmMfaPasskeyEnrollmentDocument,
  ConfirmMfaPrimaryReauthDocument,
  DisableMfaDocument,
  EnableMfaDocument,
  MfaStatusDocument,
  RegenerateBackupCodesDocument,
  RemoveMfaPasskeyCredentialDocument,
  SendMfaReauthEmailOtpDocument,
  SendMfaReauthSmsOtpDocument,
  SetMfaPrimaryFactorDocument,
  SetMfaTotpAppNameDocument,
} from '@repo/frontend-shared';
import { toast } from '@repo/sds/utils/toast';

import {
  clearPrimaryFactor,
  clearPrimaryReplaceIntent,
  clearVisualSmsEnrollment,
  consumePrimaryReplaceIntent,
  consumeReplaceSmsIntent,
  isMethodEnrolled,
  type MfaPickerMethod,
  markPrimaryReplaceIntent,
  passkeyDeviceName,
  resolveLoginPrimaryFactor,
} from './mfa-method-picker-utils';
import { readMfaSettingsError } from './mfa-settings-errors';

const RECOVERY_CODE_PATTERN = /\b[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}-[0-9A-F]{4}\b/i;
const ONE_TIME_CODE_PATTERN = /^\d{6}$/;

// Stable fallback reference: `status?.enrolledFactorChoices ?? []` would otherwise create a new
// array literal on every render (e.g. while the query is still loading), which invalidates every
// useCallback/useEffect below that lists `enrolled` as a dependency even though nothing changed.
const EMPTY_ENROLLED_FACTORS: readonly string[] = [];

export function normalizeExistingFactorProof(
  selectedFactor: string,
  submittedCode: string,
  enrolled: readonly string[],
): { existingFactor: string; code: string } {
  const recoveryMatch = submittedCode.toUpperCase().match(RECOVERY_CODE_PATTERN);
  if (recoveryMatch) {
    return { existingFactor: MFA_RECOVERY_METHOD, code: recoveryMatch[0] };
  }

  const digits = submittedCode.replace(/\s/g, '');
  if (ONE_TIME_CODE_PATTERN.test(digits)) {
    if (selectedFactor === MFA_FACTOR.Email && enrolled.includes(MFA_FACTOR.Email)) {
      return { existingFactor: MFA_FACTOR.Email, code: digits };
    }
    if (selectedFactor === MFA_FACTOR.Sms && enrolled.includes(MFA_FACTOR.Sms)) {
      return { existingFactor: MFA_FACTOR.Sms, code: digits };
    }
    if (enrolled.includes(MFA_FACTOR.Totp)) {
      return { existingFactor: MFA_FACTOR.Totp, code: digits };
    }
    if (enrolled.includes(MFA_FACTOR.Email)) {
      return { existingFactor: MFA_FACTOR.Email, code: digits };
    }
    if (enrolled.includes(MFA_FACTOR.Sms)) {
      return { existingFactor: MFA_FACTOR.Sms, code: digits };
    }
  }

  return { existingFactor: selectedFactor, code: submittedCode.trim() };
}

export type MfaSettingsTranslate = (
  key: string,
  options?: { method?: string; name?: string; seconds?: number },
) => string;

function notifyPrimaryChanged(
  method: MfaFactor,
  t: (
    key:
      | 'security.mfa.emailMethodTitle'
      | 'security.mfa.smsMethodTitle'
      | 'security.mfa.passkeyMethodTitle'
      | 'security.mfa.primaryAuthenticator'
      | 'security.mfa.primaryChangedToast',
    options?: { method?: string },
  ) => string,
): void {
  const methodLabel =
    method === MFA_FACTOR.Email
      ? t('security.mfa.emailMethodTitle')
      : method === MFA_FACTOR.Sms
        ? t('security.mfa.smsMethodTitle')
        : method === MFA_FACTOR.Passkey
          ? t('security.mfa.passkeyMethodTitle')
          : t('security.mfa.primaryAuthenticator');
  toast.success(t('security.mfa.primaryChangedToast', { method: methodLabel }));
}

export type MfaProtectedAction =
  | { type: 'choose-method' }
  | { type: 'enroll'; method: MfaFactor }
  | { type: 'disable'; factor?: MfaFactor; nextPrimary?: MfaFactor }
  | { type: 'regenerate' }
  | { type: 'remove-passkey-credential'; credentialId: string }
  | { type: 'set-primary'; method: MfaFactor }
  | { type: 'rename-totp'; name: string };

export type MfaSettingsDialog =
  | { kind: 'none' }
  | { kind: 'reauth'; next: MfaProtectedAction }
  | { kind: 'choose-method' }
  | { kind: 'disable-confirm' }
  | { kind: 'existing-factor'; existingFactor: string; next: MfaProtectedAction }
  | { kind: 'totp-manage' }
  | { kind: 'totp-remove' }
  | { kind: 'totp-setup'; otpauthUri: string; secret: string }
  | {
      kind: 'sms-setup';
      changePhone?: boolean;
      existingFactor?: string;
      existingFactorCode?: string;
      phoneNumber?: string;
    }
  | { kind: 'sms-manage' }
  | { kind: 'sms-remove' }
  | { kind: 'sms-add-method' }
  | { kind: 'passkey-setup'; addAnother?: boolean; optionsJson: string }
  | { kind: 'passkey-manage' }
  | { kind: 'passkey-remove' }
  | { kind: 'email-setup'; existingFactor?: string; existingFactorCode?: string }
  | { kind: 'email-manage' }
  | { kind: 'email-remove' }
  | { kind: 'backup-codes'; codes: string[]; variant: 'save' | 'manage' };

type MfaDisableAction = Extract<MfaProtectedAction, { type: 'disable' }>;

function applyDisableSuccess(
  next: MfaDisableAction,
  enrolled: readonly string[],
  translate: MfaSettingsTranslate,
): void {
  if (next.factor === MFA_FACTOR.Totp) {
    const remaining = enrolled.filter(factor => factor !== MFA_FACTOR.Totp);
    toastRemovedLiveFactor('totpRemovedToast', 'totpRemovedToastNoPrimary', remaining, next.nextPrimary, translate);
  }
  if (next.factor === MFA_FACTOR.Email) {
    const remaining = enrolled.filter(factor => factor !== MFA_FACTOR.Email);
    toastRemovedLiveFactor('emailRemovedToast', 'emailRemovedToastNoPrimary', remaining, next.nextPrimary, translate);
  }
  if (next.factor === MFA_FACTOR.Sms) {
    clearVisualSmsEnrollment();
    const remaining = enrolled.filter(factor => factor !== MFA_FACTOR.Sms);
    toastRemovedLiveFactor('smsRemovedToast', 'smsRemovedToast', remaining, next.nextPrimary, translate);
  }
  if (next.factor === MFA_FACTOR.Passkey) {
    const remaining = enrolled.filter(factor => factor !== MFA_FACTOR.Passkey);
    toastRemovedLiveFactor(
      'passkeysRemovedToast',
      'passkeysRemovedToastNoPrimary',
      remaining,
      next.nextPrimary,
      translate,
    );
  }
  if (!next.factor) {
    clearVisualSmsEnrollment();
    clearPrimaryFactor();
    toast.success(translate('disableOffToast'));
  }
}

function liveReplacement(method?: MfaPickerMethod): MfaFactor | undefined {
  return method === MFA_FACTOR.Totp ||
    method === MFA_FACTOR.Email ||
    method === MFA_FACTOR.Passkey ||
    method === MFA_FACTOR.Sms
    ? method
    : undefined;
}

function toastRemovedLiveFactor(
  withPrimaryKey: 'totpRemovedToast' | 'emailRemovedToast' | 'passkeysRemovedToast' | 'smsRemovedToast',
  noPrimaryKey:
    | 'totpRemovedToastNoPrimary'
    | 'emailRemovedToastNoPrimary'
    | 'passkeysRemovedToastNoPrimary'
    | 'smsRemovedToast',
  remaining: readonly string[],
  nextPrimary: MfaFactor | undefined,
  translate: MfaSettingsTranslate,
): void {
  if (nextPrimary) {
    toast.success(
      translate(withPrimaryKey, {
        method:
          nextPrimary === MFA_FACTOR.Email
            ? translate('emailMethodTitle')
            : nextPrimary === MFA_FACTOR.Sms
              ? translate('smsMethodTitle')
              : nextPrimary === MFA_FACTOR.Passkey
                ? translate('passkeyMethodTitle')
                : translate('primaryAuthenticator'),
      }),
    );
    return;
  }
  toast.success(translate(noPrimaryKey));
  if (remaining.length === 0) {
    clearPrimaryFactor();
  }
}

const MFA_REAUTH_RESUME_KEY = 'starthub.mfa.reauthResume';

function readStoredProtectedAction(raw: string): MfaProtectedAction | undefined {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || !('type' in parsed)) {
      return undefined;
    }
    const type = parsed.type;
    if (type === 'choose-method') {
      return { type: 'choose-method' };
    }
    if (type === 'regenerate') {
      return { type: 'regenerate' };
    }
    if (type === 'set-primary') {
      const method = 'method' in parsed ? parsed.method : undefined;
      if (
        method === MFA_FACTOR.Totp ||
        method === MFA_FACTOR.Email ||
        method === MFA_FACTOR.Passkey ||
        method === MFA_FACTOR.Sms
      ) {
        return { type: 'set-primary', method };
      }
    }
    if (type === 'rename-totp') {
      const name = 'name' in parsed && typeof parsed.name === 'string' ? parsed.name : undefined;
      if (name !== undefined && name.length <= 64) {
        return { type: 'rename-totp', name };
      }
    }
    if (type === 'disable') {
      const factor = 'factor' in parsed ? parsed.factor : undefined;
      const nextPrimary = 'nextPrimary' in parsed ? parsed.nextPrimary : undefined;
      const resolvedPrimary =
        nextPrimary === MFA_FACTOR.Totp ||
        nextPrimary === MFA_FACTOR.Email ||
        nextPrimary === MFA_FACTOR.Passkey ||
        nextPrimary === MFA_FACTOR.Sms
          ? nextPrimary
          : undefined;
      if (
        factor === MFA_FACTOR.Totp ||
        factor === MFA_FACTOR.Email ||
        factor === MFA_FACTOR.Passkey ||
        factor === MFA_FACTOR.Sms
      ) {
        return { type: 'disable', factor, nextPrimary: resolvedPrimary };
      }
      return { type: 'disable' };
    }
    if (type === 'enroll') {
      const method = 'method' in parsed ? parsed.method : undefined;
      if (
        method === MFA_FACTOR.Totp ||
        method === MFA_FACTOR.Email ||
        method === MFA_FACTOR.Passkey ||
        method === MFA_FACTOR.Sms
      ) {
        return { type: 'enroll', method };
      }
    }
    if (type === 'remove-passkey-credential') {
      const credentialId =
        'credentialId' in parsed && typeof parsed.credentialId === 'string' ? parsed.credentialId : undefined;
      if (credentialId) {
        return { type: 'remove-passkey-credential', credentialId };
      }
    }
    return undefined;
  } catch {
    return undefined;
  }
}

function consumeOauthReauthResume(): MfaProtectedAction | undefined {
  try {
    const raw = sessionStorage.getItem(MFA_REAUTH_RESUME_KEY);
    if (!raw) {
      return undefined;
    }
    sessionStorage.removeItem(MFA_REAUTH_RESUME_KEY);
    return readStoredProtectedAction(raw);
  } catch {
    return undefined;
  }
}

interface OauthReauthFailure {
  error: OAuthReauthError;
  action?: MfaProtectedAction;
}

const OAUTH_REAUTH_ERROR_KEYS = {
  [OAUTH_REAUTH_ERROR.AccountMismatch]: 'security.mfa.reauthOauthAccountMismatch',
  [OAUTH_REAUTH_ERROR.SessionMismatch]: 'security.mfa.reauthOauthSessionMismatch',
  [OAUTH_REAUTH_ERROR.Failed]: 'security.mfa.reauthOauthFailed',
} as const satisfies Record<OAuthReauthError, string>;

/**
 * Reads, then strips, the failure code a Google re-authentication callback sent back to this
 * page. Only known codes are shown; anything else in the URL reads as a generic failure.
 */
function consumeOauthReauthError(): OAuthReauthError | undefined {
  const url = new URL(window.location.href);
  const code = url.searchParams.get(OAUTH_REAUTH_ERROR_PARAM);
  if (code === null) {
    return undefined;
  }
  url.searchParams.delete(OAUTH_REAUTH_ERROR_PARAM);
  window.history.replaceState(window.history.state, '', `${url.pathname}${url.search}${url.hash}`);
  return parseOAuthReauthError(code) ?? OAUTH_REAUTH_ERROR.Failed;
}

/**
 * A successful Google re-authentication resumes the interrupted action. A failed one
 * reopens the re-authentication step for that action, showing why it failed.
 */
function consumeOauthReauthReturn(): { resume?: MfaProtectedAction; failure?: OauthReauthFailure } {
  const action = consumeOauthReauthResume();
  const error = consumeOauthReauthError();
  if (error === undefined) {
    return action ? { resume: action } : {};
  }
  return { failure: { error, ...(action ? { action } : {}) } };
}

export function useMfaSettings() {
  const { t } = useTranslation('common');
  const statusQuery = useQuery(MfaStatusDocument, { fetchPolicy: 'network-only' });
  const [enableMfa] = useMutation(EnableMfaDocument);
  const [confirmEnrollment] = useMutation(ConfirmMfaEnrollmentDocument);
  const [confirmPasskeyEnrollment] = useMutation(ConfirmMfaPasskeyEnrollmentDocument);
  const [confirmReauth] = useMutation(ConfirmMfaPrimaryReauthDocument);
  const [beginPasskeyReauth] = useMutation(BeginMfaPasskeyReauthDocument);
  const [sendReauthEmail] = useMutation(SendMfaReauthEmailOtpDocument);
  const [sendReauthSms] = useMutation(SendMfaReauthSmsOtpDocument);
  const [disableMfa] = useMutation(DisableMfaDocument);
  const [removePasskeyCredentialMutation] = useMutation(RemoveMfaPasskeyCredentialDocument);
  const [setMfaPrimaryFactor] = useMutation(SetMfaPrimaryFactorDocument);
  const [setMfaTotpAppName] = useMutation(SetMfaTotpAppNameDocument);
  const [regenerateBackupCodes] = useMutation(RegenerateBackupCodesDocument);

  const [oauthReturn] = useState(() => consumeOauthReauthReturn());
  const [dialog, setDialog] = useState<MfaSettingsDialog>(() =>
    oauthReturn.failure?.action ? { kind: 'reauth', next: oauthReturn.failure.action } : { kind: 'none' },
  );
  const [actionError, setActionError] = useState<string | undefined>(() =>
    oauthReturn.failure ? t(OAUTH_REAUTH_ERROR_KEYS[oauthReturn.failure.error]) : undefined,
  );
  const [busy, setBusy] = useState(false);
  const [resendAvailableAt, setResendAvailableAt] = useState<number | undefined>();
  const [, setCooldownTick] = useState(0);
  const [oauthResume, setOauthResume] = useState<MfaProtectedAction | undefined>(oauthReturn.resume);

  useEffect(() => {
    if (resendAvailableAt === undefined) {
      return undefined;
    }
    const timer = window.setInterval(() => setCooldownTick(tick => tick + 1), TIME.MILLISECONDS.PER_SECOND);
    return () => window.clearInterval(timer);
  }, [resendAvailableAt]);

  const cooldownSeconds =
    resendAvailableAt === undefined
      ? 0
      : Math.max(0, Math.ceil((resendAvailableAt - Date.now()) / TIME.MILLISECONDS.PER_SECOND));

  const markEmailSent = useCallback((waitSeconds: number = MFA_EMAIL_OTP_CONTRACT.resendIntervalSeconds) => {
    setResendAvailableAt(Date.now() + waitSeconds * TIME.MILLISECONDS.PER_SECOND);
  }, []);

  const status = statusQuery.data?.mfaStatus;
  const enrolled = status?.enrolledFactorChoices ?? EMPTY_ENROLLED_FACTORS;
  const accountPrimary = resolveLoginPrimaryFactor(enrolled, status?.primaryFactor ?? undefined);
  const totpUsage = status?.factors?.find(factor => factor.factor === MFA_FACTOR.Totp);
  const emailUsage = status?.factors?.find(factor => factor.factor === MFA_FACTOR.Email);
  const smsUsage = status?.factors?.find(factor => factor.factor === MFA_FACTOR.Sms);
  const pendingFactor = status?.pendingFactor;
  const enabled = status?.enabled === true && enrolled.length > 0;
  const hasRecentPrimaryReauth = status?.hasRecentPrimaryReauth === true;

  const clearSecrets = useCallback(() => {
    clearPrimaryReplaceIntent();
    setDialog({ kind: 'none' });
    setActionError(undefined);
    setResendAvailableAt(undefined);
  }, []);

  useEffect(() => {
    return () => {
      setDialog({ kind: 'none' });
    };
  }, []);

  const refresh = useCallback(async () => {
    await statusQuery.refetch();
  }, [statusQuery]);

  const fail = useCallback(
    (error: unknown, translate: MfaSettingsTranslate, asResend = false, invalidKey = 'invalidCode') => {
      const parsed = readMfaSettingsError(error);
      if (parsed.kind === 'reauth') {
        return 'reauth';
      }
      if (parsed.kind === 'oauth-reauth') {
        setActionError('oauth-reauth');
        return 'oauth-reauth';
      }
      if (
        parsed.kind === 'sanitized' &&
        (parsed.code === MFA_SANITIZED_ERROR_CODE.ChallengeExpired ||
          parsed.code === MFA_SANITIZED_ERROR_CODE.ChallengeConsumed)
      ) {
        setActionError(translate('expiry'));
        return 'expiry';
      }
      if (
        parsed.kind === 'sanitized' &&
        (parsed.code === MFA_SANITIZED_ERROR_CODE.ResendCooldown ||
          (parsed.code === MFA_SANITIZED_ERROR_CODE.TooManyAttempts && asResend))
      ) {
        const waitSeconds = parsed.retryAfterSeconds ?? MFA_EMAIL_OTP_CONTRACT.resendIntervalSeconds;
        markEmailSent(waitSeconds);
        setActionError(translate('emailCooldown', { seconds: waitSeconds }));
        return 'cooldown';
      }
      if (parsed.kind === 'sanitized' && parsed.code === MFA_SANITIZED_ERROR_CODE.DeliveryFailed) {
        setActionError(translate('loginDeliveryFailed'));
        return 'delivery';
      }
      if (parsed.kind === 'sanitized' && parsed.code === MFA_SANITIZED_ERROR_CODE.FactorUnavailable) {
        setActionError(translate('smsUnavailable'));
        return 'unavailable';
      }
      if (parsed.kind === 'invalid-password') {
        setActionError(translate('incorrectPassword'));
        return 'invalid-password';
      }
      if (parsed.kind === 'sanitized') {
        setActionError(translate(invalidKey));
        return 'invalid';
      }
      setActionError(translate('genericError'));
      return 'generic';
    },
    [markEmailSent],
  );

  useEffect(() => {
    if (!oauthResume || statusQuery.loading) {
      return;
    }
    const resumed = oauthResume;
    setOauthResume(undefined);
    const translate: MfaSettingsTranslate = (key, options) =>
      t(`security.mfa.${key}`, { ...options, defaultValue: key });
    // A refused resumed change (proof missing or expired) reopens re-authentication for that same change.
    const settle = (change: Promise<unknown>, next: MfaSettingsDialog) => {
      void change.then(
        () => setDialog(next),
        (error: unknown) => {
          const kind = fail(error, translate);
          if (kind === 'reauth' || kind === 'oauth-reauth') {
            setActionError(t(OAUTH_REAUTH_ERROR_KEYS[OAUTH_REAUTH_ERROR.Failed]));
            setDialog({ kind: 'reauth', next: resumed });
            return;
          }
          setDialog(next);
        },
      );
    };
    if (resumed.type === 'choose-method') {
      setDialog({ kind: 'choose-method' });
    } else if (resumed.type === 'set-primary') {
      settle(
        setMfaPrimaryFactor({ variables: { factor: resumed.method } }).then(async () => {
          notifyPrimaryChanged(resumed.method, t);
          await statusQuery.refetch();
        }),
        { kind: 'choose-method' },
      );
    } else if (resumed.type === 'rename-totp') {
      settle(
        setMfaTotpAppName({ variables: { name: resumed.name } }).then(() => statusQuery.refetch()),
        { kind: 'totp-manage' },
      );
    } else if (resumed.type === 'disable') {
      settle(
        disableMfa({
          variables: {
            confirmDisable: true,
            factor: resumed.factor,
            ...(resumed.nextPrimary ? { nextPrimary: resumed.nextPrimary } : {}),
          },
        }).then(async () => {
          applyDisableSuccess(resumed, enrolled, translate);
          await statusQuery.refetch();
        }),
        { kind: 'none' },
      );
    } else {
      setDialog({
        kind: 'existing-factor',
        existingFactor: resolveLoginPrimaryFactor(enrolled, accountPrimary),
        next: resumed,
      });
    }
  }, [accountPrimary, disableMfa, enrolled, fail, oauthResume, setMfaPrimaryFactor, setMfaTotpAppName, statusQuery, t]);

  const executeDisable = useCallback(
    async (
      next: MfaDisableAction,
      translate: MfaSettingsTranslate,
      proof?: { code: string; existingFactor: string },
    ) => {
      await disableMfa({
        variables: {
          confirmDisable: true,
          factor: next.factor,
          ...(next.nextPrimary ? { nextPrimary: next.nextPrimary } : {}),
          ...(proof ? { code: proof.code, existingFactor: proof.existingFactor } : {}),
        },
      });
      applyDisableSuccess(next, enrolled, translate);
      clearSecrets();
      await refresh();
    },
    [clearSecrets, disableMfa, enrolled, refresh],
  );

  const confirmDisable = useCallback(
    async (next: MfaDisableAction, translate: MfaSettingsTranslate) => {
      setBusy(true);
      setActionError(undefined);
      try {
        await executeDisable(next, translate);
      } catch (error) {
        const kind = fail(error, translate);
        if (kind === 'reauth') {
          setDialog({ kind: 'reauth', next });
        }
        if (kind === 'oauth-reauth') {
          setActionError(translate('reauthOauth'));
        }
      } finally {
        setBusy(false);
      }
    },
    [executeDisable, fail],
  );

  const startEnrollment = useCallback(
    async (
      method: MfaFactor,
      existingFactor?: string,
      existingFactorCode?: string,
      options?: { addAnotherPasskey?: boolean },
    ) => {
      if (method === MFA_FACTOR.Sms) {
        setDialog({
          kind: 'sms-setup',
          existingFactor,
          existingFactorCode,
        });
        return;
      }
      if (method === MFA_FACTOR.Email) {
        setDialog({ kind: 'email-setup', existingFactor, existingFactorCode });
      }
      const result = await enableMfa({
        variables: { existingFactor, existingFactorCode, method },
      });
      const setup = result.data?.enableMfa;
      if (method === MFA_FACTOR.Totp && setup?.secret && setup.otpauthUri) {
        setDialog({ kind: 'totp-setup', otpauthUri: setup.otpauthUri, secret: setup.secret });
        return;
      }
      if (method === MFA_FACTOR.Passkey && setup?.optionsJson) {
        setDialog({
          kind: 'passkey-setup',
          optionsJson: setup.optionsJson,
          ...(options?.addAnotherPasskey ? { addAnother: true } : {}),
        });
        return;
      }
      if (method === MFA_FACTOR.Email) {
        markEmailSent();
      }
    },
    [enableMfa, markEmailSent],
  );

  const sendSmsEnrollmentCode = useCallback(
    async (phoneNumber: string, translate: MfaSettingsTranslate) => {
      setBusy(true);
      setActionError(undefined);
      try {
        const existingFactor = dialog.kind === 'sms-setup' ? dialog.existingFactor : undefined;
        const existingFactorCode = dialog.kind === 'sms-setup' ? dialog.existingFactorCode : undefined;
        const changePhone = dialog.kind === 'sms-setup' ? dialog.changePhone : undefined;
        await enableMfa({
          variables: {
            existingFactor,
            existingFactorCode,
            method: MFA_FACTOR.Sms,
            phoneNumber,
          },
        });
        clearVisualSmsEnrollment();
        markEmailSent();
        setDialog({
          kind: 'sms-setup',
          phoneNumber,
          ...(changePhone ? { changePhone: true } : {}),
          ...(existingFactor ? { existingFactor } : {}),
          ...(existingFactorCode ? { existingFactorCode } : {}),
        });
        return true;
      } catch (error) {
        fail(error, translate, true);
        return false;
      } finally {
        setBusy(false);
      }
    },
    [dialog, enableMfa, fail, markEmailSent],
  );

  const confirmPasskeyRegistration = useCallback(
    async (responseJson: string, credentialName?: string) => {
      const confirmed = await confirmPasskeyEnrollment({
        variables: { responseJson, credentialName: credentialName ?? passkeyDeviceName() },
      });
      const backupCodes = confirmed.data?.confirmMfaPasskeyEnrollment.backupCodes ?? [];
      if (backupCodes.length > 0) {
        setDialog({ kind: 'backup-codes', codes: backupCodes, variant: 'save' });
        await refresh();
        return 'backup' as const;
      }
      await refresh();
      return true;
    },
    [confirmPasskeyEnrollment, refresh],
  );

  const completePrimaryRemove = useCallback(
    async (remove: MfaPickerMethod, replacement: MfaPickerMethod | undefined, translate: MfaSettingsTranslate) => {
      if (
        remove === MFA_FACTOR.Totp ||
        remove === MFA_FACTOR.Email ||
        remove === MFA_FACTOR.Passkey ||
        remove === MFA_FACTOR.Sms
      ) {
        await executeDisable(
          {
            factor: remove,
            nextPrimary: liveReplacement(replacement),
            type: 'disable',
          },
          translate,
        );
      }
    },
    [executeDisable],
  );

  const startReplacementSetup = useCallback(
    async (next: MfaPickerMethod, translate: MfaSettingsTranslate) => {
      setBusy(true);
      try {
        await startEnrollment(next);
        await refresh();
      } catch (error) {
        const kind = fail(error, translate, next === MFA_FACTOR.Email || next === MFA_FACTOR.Sms);
        if (kind === 'reauth') {
          setDialog({
            kind: 'reauth',
            next: { method: next, type: 'enroll' },
          });
          return;
        }
        clearPrimaryReplaceIntent();
      } finally {
        setBusy(false);
      }
    },
    [fail, refresh, startEnrollment],
  );

  const confirmPrimaryRemove = useCallback(
    async (remove: MfaPickerMethod, replacement: MfaPickerMethod | undefined, translate: MfaSettingsTranslate) => {
      setActionError(undefined);
      if (!replacement) {
        setBusy(true);
        try {
          await completePrimaryRemove(remove, undefined, translate);
        } catch (error) {
          const kind = fail(error, translate);
          if (kind === 'reauth') {
            setDialog({
              kind: 'reauth',
              next: {
                type: 'disable',
                factor:
                  remove === MFA_FACTOR.Totp ||
                  remove === MFA_FACTOR.Email ||
                  remove === MFA_FACTOR.Passkey ||
                  remove === MFA_FACTOR.Sms
                    ? remove
                    : undefined,
              },
            });
          }
        } finally {
          setBusy(false);
        }
        return;
      }
      if (isMethodEnrolled(replacement, enrolled)) {
        setBusy(true);
        try {
          await completePrimaryRemove(remove, replacement, translate);
        } catch (error) {
          const kind = fail(error, translate);
          if (kind === 'reauth') {
            setDialog({
              kind: 'reauth',
              next: {
                type: 'disable',
                factor:
                  remove === MFA_FACTOR.Totp ||
                  remove === MFA_FACTOR.Email ||
                  remove === MFA_FACTOR.Passkey ||
                  remove === MFA_FACTOR.Sms
                    ? remove
                    : undefined,
                nextPrimary: liveReplacement(replacement),
              },
            });
          }
        } finally {
          setBusy(false);
        }
        return;
      }
      markPrimaryReplaceIntent(remove, replacement);
      await startReplacementSetup(replacement, translate);
    },
    [completePrimaryRemove, enrolled, fail, startReplacementSetup],
  );

  const finishPrimaryReplace = useCallback(
    async (translate: MfaSettingsTranslate) => {
      const intent = consumePrimaryReplaceIntent();
      if (!intent) {
        return false;
      }
      setBusy(true);
      try {
        await completePrimaryRemove(intent.remove, intent.next, translate);
        return true;
      } catch (error) {
        fail(error, translate);
        return true;
      } finally {
        setBusy(false);
      }
    },
    [completePrimaryRemove, fail],
  );

  const enrollFactor = useCallback(
    async (method: MfaFactor, translate: MfaSettingsTranslate, options?: { addAnotherPasskey?: boolean }) => {
      setActionError(undefined);
      if (!hasRecentPrimaryReauth) {
        setDialog({
          existingFactor: resolveLoginPrimaryFactor(enrolled, accountPrimary),
          kind: 'existing-factor',
          next: { method, type: 'enroll' },
        });
        return;
      }
      setBusy(true);
      try {
        await startEnrollment(method, undefined, undefined, options);
        await refresh();
      } catch (error) {
        const kind = fail(error, translate, method === MFA_FACTOR.Email || method === MFA_FACTOR.Sms);
        if (kind === 'reauth') {
          setDialog({ kind: 'reauth', next: { method, type: 'enroll' } });
        }
      } finally {
        setBusy(false);
      }
    },
    [accountPrimary, enrolled, fail, hasRecentPrimaryReauth, refresh, startEnrollment],
  );

  const requestAction = useCallback(
    (action: MfaProtectedAction, translate: MfaSettingsTranslate) => {
      setActionError(undefined);
      if (action.type === 'choose-method') {
        setDialog(hasRecentPrimaryReauth ? { kind: 'choose-method' } : { kind: 'reauth', next: action });
        return;
      }
      if (action.type === 'enroll') {
        if (enrolled.length === 0) {
          setBusy(true);
          void startEnrollment(action.method)
            .then(() => refresh())
            .catch(error => {
              fail(error, translate, action.method === MFA_FACTOR.Email || action.method === MFA_FACTOR.Sms);
            })
            .finally(() => setBusy(false));
          return;
        }
        void enrollFactor(action.method, translate);
        return;
      }
      if (action.type === 'disable' && !action.factor) {
        setDialog({ kind: 'disable-confirm' });
        return;
      }
      if (action.type === 'regenerate') {
        setDialog({
          existingFactor: resolveLoginPrimaryFactor(enrolled, accountPrimary),
          kind: 'existing-factor',
          next: action,
        });
        return;
      }
      if (action.type === 'remove-passkey-credential') {
        if (hasRecentPrimaryReauth) {
          setBusy(true);
          void removePasskeyCredentialMutation({ variables: { credentialId: action.credentialId } })
            .then(async () => {
              const removedName =
                status?.passkeyCredentials?.find(credential => credential.id === action.credentialId)?.name ??
                passkeyDeviceName();
              toast.success(translate('passkeyRemovedToast', { name: removedName }));
              await refresh();
              setDialog({ kind: 'passkey-manage' });
            })
            .catch(error => {
              const kind = fail(error, translate);
              if (kind === 'reauth') {
                setDialog({
                  existingFactor: resolveLoginPrimaryFactor(enrolled, accountPrimary),
                  kind: 'existing-factor',
                  next: action,
                });
              }
            })
            .finally(() => setBusy(false));
          return;
        }
        setDialog({
          existingFactor: resolveLoginPrimaryFactor(enrolled, accountPrimary),
          kind: 'existing-factor',
          next: action,
        });
        return;
      }
      setDialog({ kind: 'reauth', next: action });
    },
    [
      accountPrimary,
      enrollFactor,
      enrolled,
      fail,
      hasRecentPrimaryReauth,
      refresh,
      removePasskeyCredentialMutation,
      startEnrollment,
      status?.passkeyCredentials,
    ],
  );

  const rememberOauthReauthResume = useCallback(() => {
    if (dialog.kind !== 'reauth') {
      return;
    }
    try {
      sessionStorage.setItem(MFA_REAUTH_RESUME_KEY, JSON.stringify(dialog.next));
    } catch {
      return;
    }
  }, [dialog]);

  const chooseMethod = useCallback(
    async (method: MfaFactor, translate: MfaSettingsTranslate) => {
      setActionError(undefined);
      setBusy(true);
      try {
        await startEnrollment(method);
        await refresh();
      } catch (error) {
        fail(error, translate, method === MFA_FACTOR.Email || method === MFA_FACTOR.Sms);
      } finally {
        setBusy(false);
      }
    },
    [fail, refresh, startEnrollment],
  );

  const submitReauth = useCallback(
    async (password: string | undefined, translate: MfaSettingsTranslate) => {
      setBusy(true);
      setActionError(undefined);
      try {
        if (dialog.kind !== 'reauth') {
          return;
        }
        await confirmReauth({ variables: { password } });
        if (dialog.next.type === 'choose-method') {
          await refresh();
          setDialog({ kind: 'choose-method' });
          return;
        }
        if (dialog.next.type === 'set-primary') {
          await setMfaPrimaryFactor({ variables: { factor: dialog.next.method } });
          notifyPrimaryChanged(dialog.next.method, t);
          await refresh();
          setDialog({ kind: 'choose-method' });
          return;
        }
        if (dialog.next.type === 'rename-totp') {
          await setMfaTotpAppName({ variables: { name: dialog.next.name } });
          await refresh();
          setDialog({ kind: 'totp-manage' });
          return;
        }
        if (dialog.next.type === 'disable') {
          await executeDisable(dialog.next, translate);
          return;
        }
        setDialog({
          existingFactor: resolveLoginPrimaryFactor(enrolled, accountPrimary),
          kind: 'existing-factor',
          next: dialog.next,
        });
      } catch (error) {
        const kind = fail(error, translate);
        if (kind === 'oauth-reauth') {
          setActionError(translate('reauthOauth'));
        }
      } finally {
        setBusy(false);
      }
    },
    [
      accountPrimary,
      confirmReauth,
      dialog,
      enrolled,
      executeDisable,
      fail,
      refresh,
      setMfaPrimaryFactor,
      setMfaTotpAppName,
      t,
    ],
  );

  const submitExistingFactor = useCallback(
    async (existingFactor: string, code: string, translate: MfaSettingsTranslate) => {
      if (dialog.kind !== 'existing-factor') {
        return;
      }
      setBusy(true);
      setActionError(undefined);
      try {
        let proofCode = code;
        if (existingFactor === MFA_FACTOR.Passkey) {
          const begun = await beginPasskeyReauth();
          const optionsJson = begun.data?.beginMfaPasskeyReauth;
          if (!optionsJson) {
            setActionError(translate('genericError'));
            return;
          }
          const assertion = await startAuthentication({
            optionsJSON: JSON.parse(optionsJson) as Parameters<typeof startAuthentication>[0]['optionsJSON'],
          });
          proofCode = JSON.stringify(assertion);
        }
        const proof = normalizeExistingFactorProof(existingFactor, proofCode, enrolled);
        const next = dialog.next;
        if (next.type === 'enroll') {
          await startEnrollment(next.method, proof.existingFactor, proof.code);
          await refresh();
          return;
        }
        if (next.type === 'disable') {
          await executeDisable(next, translate, { code: proof.code, existingFactor: proof.existingFactor });
          return;
        }
        if (next.type === 'remove-passkey-credential') {
          const removedName =
            status?.passkeyCredentials?.find(credential => credential.id === next.credentialId)?.name ??
            passkeyDeviceName();
          await removePasskeyCredentialMutation({
            variables: {
              credentialId: next.credentialId,
              code: proof.code,
              existingFactor: proof.existingFactor,
            },
          });
          toast.success(translate('passkeyRemovedToast', { name: removedName }));
          await refresh();
          setDialog({ kind: 'passkey-manage' });
          return;
        }
        const codes = await regenerateBackupCodes({
          variables: { code: proof.code, existingFactor: proof.existingFactor },
        });
        setDialog({
          kind: 'backup-codes',
          codes: codes.data?.regenerateBackupCodes ?? [],
          variant: 'manage',
        });
        await refresh();
      } catch (error) {
        const next = dialog.next;
        const kind = fail(
          error,
          translate,
          false,
          existingFactor === MFA_RECOVERY_METHOD ? 'invalidRecoveryCode' : 'invalidCode',
        );
        if (kind === 'reauth') {
          setDialog({ kind: 'reauth', next });
        } else if (next.type === 'enroll' && next.method === MFA_FACTOR.Email && kind === 'invalid') {
          setDialog({
            existingFactor: dialog.existingFactor,
            kind: 'existing-factor',
            next,
          });
        }
      } finally {
        setBusy(false);
      }
    },
    [
      beginPasskeyReauth,
      dialog,
      enrolled,
      executeDisable,
      fail,
      refresh,
      regenerateBackupCodes,
      removePasskeyCredentialMutation,
      startEnrollment,
      status?.passkeyCredentials,
    ],
  );

  const confirmSetup = useCallback(
    async (code: string, translate: MfaSettingsTranslate) => {
      setBusy(true);
      setActionError(undefined);
      const isTotp = dialog.kind === 'totp-setup';
      const isEmail = dialog.kind === 'email-setup';
      const isSms = dialog.kind === 'sms-setup';
      try {
        const confirmed = await confirmEnrollment({ variables: { code } });
        const intent = consumePrimaryReplaceIntent();
        const backupCodes = confirmed.data?.confirmMfaEnrollment.backupCodes ?? [];
        if (isSms) {
          clearVisualSmsEnrollment();
        }
        if (!intent && backupCodes.length > 0) {
          setDialog({ kind: 'backup-codes', codes: backupCodes, variant: 'save' });
        } else if (!intent) {
          clearSecrets();
        }
        await refresh();
        if (intent) {
          await completePrimaryRemove(intent.remove, intent.next, translate);
          return;
        }
        if (isTotp) {
          toast.success(translate('totpEnabledToast'));
        }
        if (isEmail) {
          toast.success(translate('emailEnabledToast'));
        }
        if (isSms) {
          toast.success(translate('smsEnabledToast'));
          if (dialog.kind === 'sms-setup' && dialog.changePhone) {
            setDialog({ kind: 'sms-manage' });
            return;
          }
        }
        if (consumeReplaceSmsIntent()) {
          clearVisualSmsEnrollment();
          toast.success(translate('smsRemovedToast'));
        }
      } catch (error) {
        const kind = fail(error, translate);
        if (kind === 'expiry') {
          clearSecrets();
          await refresh();
        }
      } finally {
        setBusy(false);
      }
    },
    [clearSecrets, completePrimaryRemove, confirmEnrollment, dialog, fail, refresh],
  );

  const resendEmail = useCallback(
    async (translate: MfaSettingsTranslate) => {
      setBusy(true);
      setActionError(undefined);
      try {
        if (dialog.kind === 'existing-factor') {
          if (dialog.existingFactor === MFA_FACTOR.Sms) {
            await sendReauthSms();
          } else {
            await sendReauthEmail();
          }
          markEmailSent();
          return true;
        }
        if (dialog.kind === 'email-setup') {
          await startEnrollment(MFA_FACTOR.Email, dialog.existingFactor, dialog.existingFactorCode);
          await refresh();
        }
        if (dialog.kind === 'sms-setup' && dialog.phoneNumber) {
          return await sendSmsEnrollmentCode(dialog.phoneNumber, translate);
        }
        return true;
      } catch (error) {
        fail(error, translate, true);
        return false;
      } finally {
        setBusy(false);
      }
    },
    [dialog, fail, markEmailSent, refresh, sendReauthEmail, sendReauthSms, sendSmsEnrollmentCode, startEnrollment],
  );

  const applyAccountPrimary = useCallback(
    async (method: MfaFactor, translate: MfaSettingsTranslate) => {
      if (method === accountPrimary) {
        return;
      }
      setBusy(true);
      setActionError(undefined);
      try {
        await setMfaPrimaryFactor({ variables: { factor: method } });
        notifyPrimaryChanged(method, t);
        await refresh();
      } catch (error) {
        const kind = fail(error, translate);
        if (kind === 'reauth') {
          setDialog({ kind: 'reauth', next: { method, type: 'set-primary' } });
        }
      } finally {
        setBusy(false);
      }
    },
    [accountPrimary, fail, refresh, setMfaPrimaryFactor, t],
  );

  const applyTotpAppName = useCallback(
    async (name: string, translate: MfaSettingsTranslate) => {
      const next = name.trim();
      const current = status?.totpAppName?.trim() ?? '';
      if (next === current) {
        return;
      }
      setBusy(true);
      setActionError(undefined);
      try {
        await setMfaTotpAppName({ variables: { name: next } });
        await refresh();
      } catch (error) {
        const kind = fail(error, translate);
        if (kind === 'reauth') {
          setDialog({ kind: 'reauth', next: { name: next, type: 'rename-totp' } });
        }
      } finally {
        setBusy(false);
      }
    },
    [fail, refresh, setMfaTotpAppName, status?.totpAppName],
  );

  return {
    accountPrimary,
    actionError,
    applyAccountPrimary,
    applyTotpAppName,
    busy,
    chooseMethod,
    clearSecrets,
    confirmDisable,
    confirmPasskeyRegistration,
    confirmPrimaryRemove,
    confirmSetup,
    cooldownSeconds,
    dialog,
    enabled,
    enrollFactor,
    enrolled,
    finishPrimaryReplace,
    totpEnrolledAt: totpUsage?.enrolledAt,
    totpLastUsedAt: totpUsage?.lastUsedAt,
    emailEnrolledAt: emailUsage?.enrolledAt,
    emailLastUsedAt: emailUsage?.lastUsedAt,
    smsEnrolledAt: smsUsage?.enrolledAt,
    smsLastUsedAt: smsUsage?.lastUsedAt,
    smsPhoneMasked: status?.smsPhoneMasked,
    loading: statusQuery.loading,
    loadError: statusQuery.error,
    passkeyCredentials: status?.passkeyCredentials ?? [],
    pendingFactor,
    remainingBackupCodes: status?.remainingBackupCodes ?? 0,
    backupGeneratedAt: status?.backupGeneratedAt,
    rememberOauthReauthResume,
    requestAction,
    removePasskeyCredential: (credentialId: string) =>
      requestAction({ type: 'remove-passkey-credential', credentialId }, (key, options) =>
        t(`security.mfa.${key}`, { ...options, defaultValue: key }),
      ),
    resendEmail,
    sendSmsEnrollmentCode,
    setActionError,
    setDialog,
    status,
    submitExistingFactor,
    submitReauth,
  };
}
